import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import logger from '../../utils/logger';

interface CollectedSignature {
    signer: string;
    signature: string;
}

interface SignatureContextType {
    signatures: { [address: string]: string };
    addSignature: (address: string, signature: string) => void;
    clearSignatures: () => void;
    hasSignature: (address: string) => boolean;
    getAllSignatures: () => CollectedSignature[];
    transactionData: {
        inputs: any[];
        contract: string;
        functionName: string;
    } | null;
    setTransactionData: (data: { inputs: any[]; contract: string; functionName: string }) => void;
    signatureProgress: {
        collectedCount: number;
        requiredCount: number;
        owners: string[];
        completedSigners: string[];
    };
    updateSignatureProgress: (progress: {
        collectedCount?: number;
        requiredCount?: number;
        owners?: string[];
        completedSigners?: string[];
    }) => void;
    clearAllStoredData: () => void;
}

const SignatureContext = createContext<SignatureContextType | undefined>(undefined);

// Control debug logging
const DEBUG = false;
const log = (...args: any[]) => {
    if (DEBUG) logger.log('[SignatureContext]', ...args);
};

export const SignatureProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [signatures, setSignatures] = useState<{ [address: string]: string }>({});
    const [transactionData, setTransactionData] = useState<{
        inputs: any[];
        contract: string;
        functionName: string;
    } | null>(null);

    const [signatureProgress, setSignatureProgress] = useState({
        collectedCount: 0,
        requiredCount: 0,
        owners: [] as string[],
        completedSigners: [] as string[]
    });

    // Enhanced restoration logic
    useEffect(() => {
        const restorePersistedState = () => {
            log('Restoring signature state from localStorage');
            try {
                // Load signatures
                const savedSignatures = localStorage.getItem('multisig_signatures');
                if (savedSignatures) {
                    const parsedSignatures = JSON.parse(savedSignatures);
                    log(`Found ${Object.keys(parsedSignatures).length} signatures in localStorage`);
                    setSignatures(parsedSignatures);
                }

                // Load transaction data
                const savedTransactionData = localStorage.getItem('multisig_transaction_data');
                if (savedTransactionData) {
                    setTransactionData(JSON.parse(savedTransactionData));
                    log('Restored transaction data');
                }

                // Load signature progress
                const savedProgress = localStorage.getItem('multisig_signature_progress');
                if (savedProgress) {
                    const parsedProgress = JSON.parse(savedProgress);
                    setSignatureProgress(parsedProgress);
                    log(`Restored progress: ${parsedProgress.collectedCount}/${parsedProgress.requiredCount} signatures`);
                }
            } catch (error) {
                logger.error('Error loading signature data from localStorage:', error);
            }
        };

        // Call immediately on mount
        restorePersistedState();

        // Also listen for wallet changes to ensure we restore state properly
        const handleWalletChanged = () => {
            log('Wallet changed - re-syncing signature state');
            setTimeout(restorePersistedState, 300);
        };

        window.addEventListener('walletChanged', handleWalletChanged);
        return () => {
            window.removeEventListener('walletChanged', handleWalletChanged);
        };
    }, []);

    // Add this event listener in the SignatureProvider component
    useEffect(() => {
        // Set up event listeners for wallet changes
        const handleWalletChanging = (event: Event) => {
            log('Wallet changing - preserving signature state');

            // Save the current state comprehensively
            const state = {
                signatures: signatures,
                transactionData,
                signatureProgress,
                timestamp: Date.now()
            };

            // Store as JSON in localStorage
            localStorage.setItem('multisig_signature_state', JSON.stringify(state));
        };

        // Set up restoration when wallet changed
        const handleWalletChanged = (event: Event) => {
            log('Wallet changed - restoring signature state');
            setTimeout(() => {
                // Try to restore from saved state
                const savedState = localStorage.getItem('multisig_signature_state');
                if (savedState) {
                    try {
                        const state = JSON.parse(savedState);

                        // Restore all components of state
                        setSignatures(state.signatures || {});
                        setTransactionData(state.transactionData || null);
                        setSignatureProgress(state.signatureProgress || {
                            collectedCount: 0,
                            requiredCount: 0,
                            owners: [],
                            completedSigners: []
                        });

                        log('Signature state restored successfully');
                    } catch (error) {
                        logger.error('Error restoring signature state:', error);
                    }
                }
            }, 300); // Small delay to ensure wallet state is settled
        };

        window.addEventListener('walletChanging', handleWalletChanging);
        window.addEventListener('walletChanged', handleWalletChanged);

        return () => {
            window.removeEventListener('walletChanging', handleWalletChanging);
            window.removeEventListener('walletChanged', handleWalletChanged);
        };
    }, [signatures, transactionData, signatureProgress]);

    // Enhanced persistence for signatures
    useEffect(() => {
        if (Object.keys(signatures).length > 0) {
            log(`Persisting ${Object.keys(signatures).length} signatures to localStorage`);
            localStorage.setItem('multisig_signatures', JSON.stringify(signatures));
        }
    }, [signatures]);

    // Persistence for transaction data
    useEffect(() => {
        if (transactionData) {
            log('Persisting transaction data to localStorage');
            localStorage.setItem('multisig_transaction_data', JSON.stringify(transactionData));
        }
    }, [transactionData]);

    // Persistence for signature progress
    useEffect(() => {
        log(`Persisting signature progress: ${signatureProgress.collectedCount}/${signatureProgress.requiredCount}`);
        localStorage.setItem('multisig_signature_progress', JSON.stringify(signatureProgress));

        // Also update the signature flow active flag
        if (signatureProgress.collectedCount > 0) {
            localStorage.setItem('signature_flow_active', 'true');
        } else {
            localStorage.removeItem('signature_flow_active');
        }
    }, [signatureProgress]);

    const addSignature = (address: string, signature: string) => {
        log(`Adding signature for ${address.substring(0, 10)}...`);

        // Normalize address to lowercase for consistency
        const normalizedAddress = address.toLowerCase();

        // Update signatures state
        setSignatures(prev => ({
            ...prev,
            [normalizedAddress]: signature
        }));

        // Update signature progress
        setSignatureProgress(prev => {
            // Make sure we don't add duplicates to completedSigners
            const currentSigners = new Set(prev.completedSigners);
            currentSigners.add(normalizedAddress);

            return {
                ...prev,
                collectedCount: currentSigners.size,
                completedSigners: Array.from(currentSigners)
            };
        });

        // Directly update localStorage for redundancy
        try {
            const existingSignatures = JSON.parse(localStorage.getItem('multisig_signatures') || '{}');
            existingSignatures[normalizedAddress] = signature;
            localStorage.setItem('multisig_signatures', JSON.stringify(existingSignatures));
            log('Signature added to localStorage');
        } catch (e) {
            logger.error('Error updating localStorage with new signature:', e);
        }
    };

    const clearSignatures = () => {
        log('Clearing all signatures');
        setSignatures({});
        setSignatureProgress(prev => ({
            ...prev,
            collectedCount: 0,
            completedSigners: []
        }));

        // Clear from localStorage too
        localStorage.removeItem('multisig_signatures');
        localStorage.removeItem('multisig_signature_progress');
        localStorage.removeItem('signature_flow_active');
        localStorage.removeItem('signature_state_before_collect');
        localStorage.removeItem('current_signature_flow_key');

        // Clear any other signature-related keys that might be stored
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (
                key.startsWith('signature_flow_') ||
                key.includes('multisig_') ||
                key.includes('signature_')
            )) {
                keysToRemove.push(key);
            }
        }

        // Remove all identified keys
        keysToRemove.forEach(key => localStorage.removeItem(key));

        log('All signature data cleared from localStorage');
    };

    const hasSignature = (address: string): boolean => {
        // Normalize address to lowercase for consistent comparison
        const normalizedAddress = address.toLowerCase();
        return !!signatures[normalizedAddress];
    };

    const getAllSignatures = (): CollectedSignature[] => {
        return Object.entries(signatures).map(([signer, signature]) => ({
            signer,
            signature
        }));
    };

    const updateSignatureProgress = (progress: {
        collectedCount?: number;
        requiredCount?: number;
        owners?: string[];
        completedSigners?: string[];
    }) => {
        log('Updating signature progress:', progress);
        setSignatureProgress(prev => ({
            ...prev,
            ...progress
        }));
    };

    // Comprehensive clean-up
    const clearAllStoredData = () => {
        log('Clearing all signature data');
        localStorage.removeItem('multisig_signatures');
        localStorage.removeItem('multisig_transaction_data');
        localStorage.removeItem('multisig_signature_progress');
        localStorage.removeItem('signature_flow_active');
        localStorage.removeItem('signature_state_before_collect');
        localStorage.removeItem('function_inputs');
        clearSignatures();
        setTransactionData(null);
        setSignatureProgress({
            collectedCount: 0,
            requiredCount: 0,
            owners: [],
            completedSigners: []
        });
    };

    return (
        <SignatureContext.Provider
            value={{
                signatures,
                addSignature,
                clearSignatures,
                hasSignature,
                getAllSignatures,
                transactionData,
                setTransactionData,
                signatureProgress,
                updateSignatureProgress,
                clearAllStoredData
            }}
        >
            {children}
        </SignatureContext.Provider>
    );
};

export const useSignatureContext = () => {
    const context = useContext(SignatureContext);
    if (!context) {
        throw new Error('useSignatureContext must be used within a SignatureProvider');
    }
    return context;
};
